import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiBookOpen,
  FiDownload,
  FiFileText,
  FiPrinter,
  FiShare2,
  FiX,
} from "react-icons/fi";
import { LanguageContext } from "./LanguageContext";
import enTranslations from "./translations/en.json";
import guTranslations from "./translations/gu.json";
import hinTranslations from "./translations/hi.json";
import mrTranslations from "./translations/mr.json";
import { getDataQr } from "./services/attachmentService";
import "./DataView.css";

const hasValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const displayValue = (value, fallback = "NA") =>
  hasValue(value) ? String(value).trim() : fallback;

const formatDate = (value) => {
  if (!value) return "NA";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "NA";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const parseLawDetails = (value) => {
  if (!hasValue(value)) return [];

  try {
    let parsed = typeof value === "string" ? JSON.parse(value) : value;

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (Array.isArray(parsed)) {
      return parsed.map((entry) => ({
        act: displayValue(entry?.act || entry?.name),
        sections: displayValue(
          entry?.section || entry?.sections || entry?.section_of_law
        ),
      }));
    }

    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed).map(([act, sections]) => ({
        act: displayValue(act),
        sections: Array.isArray(sections)
          ? sections.map((section) => displayValue(section)).join(", ")
          : displayValue(sections),
      }));
    }
  } catch (error) {
    return [{ act: "Act / Section", sections: displayValue(value) }];
  }

  return [];
};

const openPrintWindow = ({ title, markup, styles }) => {
  if (!markup) return;

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  printWindow.opener = null;
  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>${styles}</style>
      </head>
      <body>${markup}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
};

function DetailItem({
  itemClassName = "",
  label,
  value,
  valueClassName = "",
}) {
  return (
    <div
      className={`data-view-detail-item ${itemClassName}`.trim()}
    >
      <dt>{label}</dt>
      <dd className={valueClassName}>{displayValue(value)}</dd>
    </div>
  );
}

function DataViewPopup({ data = {}, qrCodeUrl, onClose }) {
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(true);
  const [qrCodeError, setQrCodeError] = useState("");
  const [actionFeedback, setActionFeedback] = useState("");
  const { selectedLanguage } = useContext(LanguageContext);
  const closeButtonRef = useRef(null);
  const detailsPrintRef = useRef(null);
  const qrPrintRef = useRef(null);

  const translations = {
    en: enTranslations,
    gu: guTranslations,
    hi: hinTranslations,
    mr: mrTranslations,
  };
  const activeTranslations = translations[selectedLanguage] || enTranslations;
  const translatedText = (key) => activeTranslations[key] || key;
  const lawDetails = useMemo(
    () => parseLawDetails(data.section_of_law),
    [data.section_of_law]
  );

  useEffect(() => {
    const previousActiveElement = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus()
    );
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus?.();
    };
  }, [onClose]);

  useEffect(() => {
    let isActive = true;
    let objectUrl = null;

    const fetchQrCode = async () => {
      setQrCodeLoading(true);
      setQrCodeError("");

      if (!data?.id) {
        setQrCodeLoading(false);
        setQrCodeError("QR code is not available for this property.");
        return;
      }

      try {
        const qrBlob = await getDataQr(data.id);
        if (!qrBlob || !isActive) return;

        objectUrl = URL.createObjectURL(qrBlob);
        setQrCodeImage(objectUrl);
      } catch (error) {
        console.error("Error fetching QR code:", error);
        if (isActive) {
          setQrCodeError("Unable to load the QR code. Please try again.");
        }
      } finally {
        if (isActive) {
          setQrCodeLoading(false);
        }
      }
    };

    fetchQrCode();

    return () => {
      isActive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [data?.id, qrCodeUrl]);

  const getQrFilename = () => {
    const propertyId = displayValue(data.serial_number, "property").replace(
      /[^a-z0-9_-]/gi,
      "-"
    );
    return `case-property-${propertyId}-qr.png`;
  };

  const handleDownload = () => {
    if (!qrCodeImage) return;

    const link = document.createElement("a");
    link.href = qrCodeImage;
    link.download = getQrFilename();
    link.click();
    setActionFeedback("QR code downloaded.");
  };

  const handleShare = async () => {
    if (!qrCodeImage) return;

    try {
      const response = await fetch(qrCodeImage);
      const blob = await response.blob();
      const file = new File([blob], getQrFilename(), {
        type: blob.type || "image/png",
      });
      const shareData = {
        files: [file],
        title: `Case Property ${displayValue(data.serial_number, "")}`,
        text: "Case property QR code",
      };

      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        setActionFeedback("QR code shared.");
        return;
      }

      if (navigator.clipboard && qrCodeUrl) {
        await navigator.clipboard.writeText(qrCodeUrl);
        setActionFeedback("QR link copied to the clipboard.");
        return;
      }

      handleDownload();
      setActionFeedback("Sharing is unavailable, so the QR code was downloaded.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error sharing QR code:", error);
        setActionFeedback("Unable to share the QR code.");
      }
    }
  };

  const handlePrintData = () => {
    openPrintWindow({
      title: `Case Property ${displayValue(data.serial_number, "Details")}`,
      markup: detailsPrintRef.current?.outerHTML,
      styles: `
        * { box-sizing: border-box; }
        body { margin: 0; padding: 28px; color: #17233a; font: 13px Arial, sans-serif; }
        .data-view-summary-strip { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid #d9e0e7; }
        .data-view-summary-item { padding: 14px; border-right: 1px solid #d9e0e7; }
        .data-view-summary-item:last-child { border-right: 0; }
        .data-view-summary-label, dt { color: #667386; font-size: 11px; }
        .data-view-summary-value { display: block; margin-top: 5px; font-size: 17px; font-weight: 700; }
        .data-view-section { margin-top: 14px; padding: 16px; border: 1px solid #d9e0e7; }
        .data-view-section-heading { margin: 0 0 13px; padding-bottom: 10px; border-bottom: 1px solid #d9e0e7; font-size: 15px; }
        .data-view-section-icon { display: none; }
        .data-view-detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px 28px; }
        .data-view-detail-item { display: grid; grid-template-columns: 150px 1fr; gap: 10px; }
        dt, dd { margin: 0; }
        dd { font-weight: 600; }
        .data-view-law-table { width: 100%; border-collapse: collapse; }
        .data-view-law-table th, .data-view-law-table td { padding: 9px; border: 1px solid #d9e0e7; text-align: left; }
        .data-view-description { margin: 0; line-height: 1.65; white-space: pre-wrap; }
        .data-view-status-badge { color: #176b4d; }
      `,
    });
  };

  const handlePrintQRCode = () => {
    openPrintWindow({
      title: `Case Property ${displayValue(data.serial_number, "QR")}`,
      markup: qrPrintRef.current?.outerHTML,
      styles: `
        body { min-height: 100vh; margin: 0; display: grid; place-items: center; font-family: Arial, sans-serif; }
        .data-view-qr-card { padding: 18px; border: 1px solid #d9e0e7; text-align: center; }
        .data-view-qr-code-img { display: block; width: 280px; height: 280px; object-fit: contain; }
        .data-view-qr-caption { margin: 12px 0 0; color: #526072; font-size: 12px; }
        .data-view-qr-placeholder { display: none; }
      `,
    });
  };

  const qrActionsDisabled = qrCodeLoading || !qrCodeImage;

  return (
    <section
      aria-label="Case Property Details"
      aria-modal="true"
      className="data-view-popup-content"
      onClick={(event) => event.stopPropagation()}
      role="dialog"
    >
      <button
        aria-label="Close case property details"
        className="data-view-close-icon"
        onClick={onClose}
        ref={closeButtonRef}
        type="button"
      >
        <FiX />
      </button>

      <div className="data-view-dialog-body">
        <main className="data-view-main" ref={detailsPrintRef}>
          <div className="data-view-summary-strip">
            <div className="data-view-summary-item">
              <span className="data-view-summary-label">
                {translatedText("guna_number")}
              </span>
              <strong className="data-view-summary-value">
                {displayValue(data.guna_number)}
              </strong>
            </div>
            <div className="data-view-summary-item">
              <span className="data-view-summary-label">
                {translatedText("exhibit_number")}
              </span>
              <strong className="data-view-summary-value">
                {displayValue(data.exhibit_number)}
              </strong>
            </div>
            <div className="data-view-summary-item">
              <span className="data-view-summary-label">
                {translatedText("status_of_muddamaal")}
              </span>
              <span className="data-view-status-badge">
                {displayValue(data.status_of_muddamaal)}
              </span>
            </div>
          </div>

          <section className="data-view-section">
            <h3 className="data-view-section-heading">
              <FiFileText className="data-view-section-icon" />
              Property Summary
            </h3>
            <dl className="data-view-detail-grid">
              <DetailItem
                label={translatedText("guna_number")}
                value={data.guna_number}
              />
              <DetailItem
                label={translatedText("quantity")}
                value={data.quantity}
              />
              <DetailItem
                label={translatedText("date_of_seizure")}
                value={formatDate(data.date_of_seizure)}
              />
              <DetailItem label="Unit" value={data.unit} />
              <DetailItem
                label={translatedText("exhibit_number")}
                value={data.exhibit_number}
              />
              <DetailItem
                label={translatedText("price")}
                value={data.price}
              />
              <DetailItem
                label={translatedText("muddamaal_type")}
                value={data.muddamaal_type}
              />
              <DetailItem
                label={translatedText("status_of_muddamaal")}
                value={data.status_of_muddamaal}
                valueClassName="data-view-positive-value"
              />
              {data.muddamaal_type === "વાહન" ? (
                <DetailItem
                  label={translatedText("vehicle_reason")}
                  value={data.vehicle_reason}
                />
              ) : null}
              <DetailItem
                itemClassName="data-view-detail-item--description"
                label={translatedText("description")}
                value={data.description}
              />
            </dl>
          </section>

          <section className="data-view-section">
            <h3 className="data-view-section-heading">
              <FiBookOpen className="data-view-section-icon" />
              Legal Information
            </h3>
            <div className="data-view-law-wrap">
              <table className="data-view-law-table">
                <colgroup>
                  <col className="data-view-law-act-column" />
                  <col className="data-view-law-section-column" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Act</th>
                    <th scope="col">Section of Law</th>
                  </tr>
                </thead>
                <tbody>
                  {lawDetails.length ? (
                    lawDetails.map((entry, index) => (
                      <tr key={`${entry.act}-${entry.sections}-${index}`}>
                        <td>{entry.act}</td>
                        <td>{entry.sections}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2">No act or section entered.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <dl className="data-view-detail-grid data-view-detail-grid--single">
              <DetailItem
                label={translatedText("specifed_act")}
                value={data.specify_act}
              />
            </dl>
          </section>
        </main>

        <aside className="data-view-aside">
          <section className="data-view-aside-section">
            <h3>QR Access</h3>
            <div className="data-view-qr-card" ref={qrPrintRef}>
              <div className="data-view-qr-code-container">
                {qrCodeLoading ? (
                  <div
                    aria-label="Loading QR code"
                    className="data-view-qr-placeholder"
                    role="status"
                  >
                    <span className="data-view-qr-spinner" />
                    Loading QR code
                  </div>
                ) : qrCodeImage ? (
                  <img
                    alt={`QR code for property ${displayValue(
                      data.serial_number,
                      ""
                    )}`}
                    className="data-view-qr-code-img"
                    src={qrCodeImage}
                  />
                ) : (
                  <div className="data-view-qr-placeholder">
                    {qrCodeError || "QR code is unavailable."}
                  </div>
                )}
              </div>
              <p className="data-view-qr-caption">
                Scan to access the property reference.
              </p>
            </div>
          </section>

          <section className="data-view-aside-section data-view-actions">
            <h3>Actions</h3>
            <button
              className="data-view-action data-view-action--primary"
              disabled={qrActionsDisabled}
              onClick={handleDownload}
              type="button"
            >
              <FiDownload />
              Download QR code
            </button>
            <button
              className="data-view-action"
              disabled={qrActionsDisabled}
              onClick={handleShare}
              type="button"
            >
              <FiShare2 />
              Share QR code
            </button>
            <div className="data-view-action-separator" />
            <button
              className="data-view-action"
              disabled={qrActionsDisabled}
              onClick={handlePrintQRCode}
              type="button"
            >
              <FiPrinter />
              Print QR
            </button>
            <button
              className="data-view-action"
              onClick={handlePrintData}
              type="button"
            >
              <FiFileText />
              Print details
            </button>
            <p
              aria-live="polite"
              className="data-view-action-feedback"
              role="status"
            >
              {actionFeedback}
            </p>
          </section>

        </aside>
      </div>
    </section>
  );
}

export default DataViewPopup;
